import { createSlice } from '@reduxjs/toolkit';
import { RootState, useCreateUseState } from '@hooks/redux/redux';
import { IUsersData } from '@/entities/admin-app/users';
import { store } from '@/store';
import { homeFactory } from '@/services/factories/homeFactory';
import { UserTariffParams } from '@/entities/admin-app/user';

type WidgetTypes =
  | 'usersWidget'
  | 'mailWidget'
  | 'roleWidget'
  | 'paymentWidget'
  | 'servicesWidget'
  | 'diskSpaceWidget'
  | 'filialWidget';

const listOfWidgets: WidgetTypes[] = [
  'usersWidget',
  'mailWidget',
  'roleWidget',
  'paymentWidget',
  'servicesWidget',
  'diskSpaceWidget',
  'filialWidget'
];

// Локальные ключи для localStorage
export const LOCAL_STORE = {
  usersWidget: listOfWidgets[0],
  mailWidget: listOfWidgets[1],
  roleWidget: listOfWidgets[2],
  paymentWidget: listOfWidgets[3],
  servicesWidget: listOfWidgets[4],
  diskSpaceWidget: listOfWidgets[5],
  filialWidget: listOfWidgets[6],
  loadedWidget: 'loadedWidget'
} as const;

// Получение данных из localStorage
const getLocalStorageData = <T>(key: string, value?: T) => {
  const data = window.localStorage.getItem(key);
  return data ? JSON.parse(data) : value;
};

// Структура стейта виджета
export interface WidgetState<T> {
  data: T | undefined;
  isLoading: boolean;
}

// Структура общего состояния
interface State {
  usersWidget: WidgetState<IUsersData>;
  mailWidget: WidgetState<any>;
  roleWidget: WidgetState<any>;
  paymentWidget: WidgetState<any>;
  servicesWidget: WidgetState<any>;
  diskSpaceWidget: WidgetState<any>;
  filialWidget: WidgetState<any>;
}

const initialState: State = {
  usersWidget: {
    data: getLocalStorageData(LOCAL_STORE.usersWidget),
    isLoading: false
  },
  mailWidget: {
    data: getLocalStorageData(LOCAL_STORE.mailWidget),
    isLoading: false
  },
  roleWidget: {
    data: getLocalStorageData(LOCAL_STORE.roleWidget),
    isLoading: false
  },
  paymentWidget: {
    data: getLocalStorageData(LOCAL_STORE.paymentWidget),
    isLoading: false
  },
  servicesWidget: {
    data: getLocalStorageData(LOCAL_STORE.servicesWidget),
    isLoading: false
  },
  diskSpaceWidget: {
    data: getLocalStorageData(LOCAL_STORE.diskSpaceWidget),
    isLoading: false
  },
  filialWidget: {
    data: getLocalStorageData(LOCAL_STORE.filialWidget),
    isLoading: false
  }
};
const homeWidget = createSlice({
  name: 'homeWidget',
  initialState,
  reducers: {
    setWidgetData(state: any, action) {
      const { key, data } = action.payload;

      state[key].data = data;
    },
    setWidgetLoading(state: any, action) {
      const { key, isLoading } = action.payload;

      state[key].isLoading = isLoading;
    }
  }
});
const { setWidgetData, setWidgetLoading } = homeWidget.actions;

const LoadWidgetData = async (filialId: number, paymentsDate: string | null) => {
  listOfWidgets.forEach((key: WidgetTypes) =>
    store.dispatch(setWidgetLoading({ key, isLoading: true }))
  );

  const data = await homeFactory().LoadDashboardWidgets(filialId, paymentsDate);

  if (data) {
    listOfWidgets.forEach((key: WidgetTypes) => {
      store.dispatch(setWidgetData({ key, data: data[key] || undefined }));
    });
  }

  listOfWidgets.forEach((key: WidgetTypes) =>
    store.dispatch(setWidgetLoading({ key, isLoading: false }))
  );
};

const LoadUsersSection = async (id: number) => {
  store.dispatch(setWidgetLoading({ key: 'usersWidget', isLoading: true }));

  homeFactory()
    .LoadUsersSection(id)
    .then((data) => store.dispatch(setWidgetData({ key: 'usersWidget', data })))
    .finally(() => store.dispatch(setWidgetLoading({ key: 'usersWidget', isLoading: false })));
};
const LoadMailSection = async (id: number) => {
  store.dispatch(setWidgetLoading({ key: 'usersWidget', isLoading: true }));

  homeFactory()
    .LoadMailSection(id)
    .then((data) => store.dispatch(setWidgetData({ key: 'mailWidget', data })))
    .finally(() => store.dispatch(setWidgetLoading({ key: 'mailWidget', isLoading: false })));
};
const LoadRoleSection = async () => {
  store.dispatch(setWidgetLoading({ key: 'roleWidget', isLoading: true }));

  homeFactory()
    .LoadRoleSection({
      Count: 10,
      Offset: 0,
      Query: '',
      FilterConditions: []
    })
    .then((data) => store.dispatch(setWidgetData({ key: 'roleWidget', data })))
    .finally(() => store.dispatch(setWidgetLoading({ key: 'roleWidget', isLoading: false })));
};
const LoadPaymentSection = async ({ id, paymentsDate }: UserTariffParams) => {
  store.dispatch(setWidgetLoading({ key: 'paymentWidget', isLoading: true }));

  homeFactory()
    .LoadPaymentSection({ id: id ?? 0, paymentsDate: paymentsDate })
    .then((data) => store.dispatch(setWidgetData({ key: 'paymentWidget', data })))
    .finally(() => store.dispatch(setWidgetLoading({ key: 'paymentWidget', isLoading: false })));
};
const LoadServicesSection = async (id: number) => {
  store.dispatch(setWidgetLoading({ key: 'servicesWidget', isLoading: true }));

  homeFactory()
    .LoadServicesSection(id)
    .then((data) => store.dispatch(setWidgetData({ key: 'servicesWidget', data })))
    .finally(() => store.dispatch(setWidgetLoading({ key: 'servicesWidget', isLoading: false })));
};
const LoadDiskSpaceSection = async (id: number) => {
  store.dispatch(setWidgetLoading({ key: 'diskSpaceWidget', isLoading: true }));

  homeFactory()
    .LoadDiskSpaceSection(id)
    .then((data) => store.dispatch(setWidgetData({ key: 'diskSpaceWidget', data })))
    .finally(() => store.dispatch(setWidgetLoading({ key: 'diskSpaceWidget', isLoading: false })));
};
const LoadFilialSection = async () => {
  store.dispatch(setWidgetLoading({ key: 'filialWidget', isLoading: true }));

  homeFactory()
    .LoadFilialSection({
      Count: 10,
      Offset: 0,
      Query: '',
      SkipCustomers: []
    })
    .then((data) => store.dispatch(setWidgetData({ key: 'filialWidget', data })))
    .finally(() => store.dispatch(setWidgetLoading({ key: 'filialWidget', isLoading: false })));
};

const actions = {
  ...homeWidget.actions,
  LoadWidgetData,
  LoadUsersSection,
  LoadMailSection,
  LoadRoleSection,
  LoadPaymentSection,
  LoadServicesSection,
  LoadDiskSpaceSection,
  LoadFilialSection
};

const useHomeWidgetSlice = () => {
  return {
    actions,
    useUsersWidget: () =>
      useCreateUseState<WidgetState<IUsersData>>(
        (state: RootState) => state.homeWidget.usersWidget,
        (data) => setWidgetData({ key: LOCAL_STORE.usersWidget, data }),
        LOCAL_STORE.usersWidget
      ),
    useMailWidget: () =>
      useCreateUseState<WidgetState<any>>(
        (state: RootState) => state.homeWidget.mailWidget,
        (data) => setWidgetData({ key: LOCAL_STORE.mailWidget, data }),
        LOCAL_STORE.mailWidget
      ),
    useRoleWidget: () =>
      useCreateUseState<WidgetState<any>>(
        (state: RootState) => state.homeWidget.roleWidget,
        (data) => setWidgetData({ key: LOCAL_STORE.roleWidget, data }),
        LOCAL_STORE.roleWidget
      ),
    usePaymentWidget: () =>
      useCreateUseState<WidgetState<any>>(
        (state: RootState) => state.homeWidget.paymentWidget,
        (data) => setWidgetData({ key: LOCAL_STORE.paymentWidget, data }),
        LOCAL_STORE.paymentWidget
      ),
    useServicesWidget: () =>
      useCreateUseState<WidgetState<any>>(
        (state: RootState) => state.homeWidget.servicesWidget,
        (data) => setWidgetData({ key: LOCAL_STORE.servicesWidget, data }),
        LOCAL_STORE.servicesWidget
      ),
    useDiskSpaceWidget: () =>
      useCreateUseState<WidgetState<any>>(
        (state: RootState) => state.homeWidget.diskSpaceWidget,
        (data) => setWidgetData({ key: LOCAL_STORE.diskSpaceWidget, data }),
        LOCAL_STORE.diskSpaceWidget
      ),
    useFilialWidget: () =>
      useCreateUseState<WidgetState<any>>(
        (state: RootState) => state.homeWidget.filialWidget,
        (data) => setWidgetData({ key: LOCAL_STORE.filialWidget, data }),
        LOCAL_STORE.filialWidget
      )
  };
};

export const homeWidgetReducer = homeWidget.reducer;

export default useHomeWidgetSlice;
